// src/components/module/ventas/BuscadorArticulos.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Alert,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon, Add as AddIcon, Remove as RemoveIcon,
  Close as CloseIcon, CheckCircle, Inventory,
} from "@mui/icons-material";
import axios from "axios";
import { formatMoney } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";

interface Articulo { Id: number; Nombre: string; EAN: string; PrecioVenta: number; stock: number; Descripcion?: string; Categoria?: string; }
interface BuscadorArticulosProps { onArticuloSeleccionado: (articulo: Articulo, cantidad: number) => void; disabled?: boolean; idSucursal: number; esCotizacion?: boolean; }

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4020";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020", hover: "rgba(255,255,255,0.04)" },
};

export const BuscadorArticulos: React.FC<BuscadorArticulosProps> = ({ onArticuloSeleccionado, disabled = false, idSucursal, esCotizacion = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [articulos, setArticulos] = useState<Articulo[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState<Articulo | null>(null);
  const [cantidad, setCantidad] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  useEffect(() => {
    if (searchTerm.length < 2) { setArticulos([]); setShowResults(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await axios.post(`${API_URL}/almacen/articulos/buscar`, { termino: searchTerm, idSucursal }); setArticulos(data.slice(0, 8)); setShowResults(true); }
      catch { setArticulos([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, idSucursal]);

  const handleSelect = (a: Articulo) => { setSelected(a); setCantidad(1); setModalOpen(true); setShowResults(false); };
  const handleAdd = () => { if (selected && cantidad > 0) { onArticuloSeleccionado(selected, cantidad); setModalOpen(false); setSelected(null); setCantidad(1); setSearchTerm(""); setTimeout(() => inputRef.current?.focus(), 0); } };
  const handleClose = () => { setModalOpen(false); setSelected(null); setCantidad(1); setTimeout(() => inputRef.current?.focus(), 0); };

  const handleBlur = (e: React.FocusEvent) => {
    if (disabled || modalOpen) return;
    // Si el foco va a un elemento dentro del componente (ej: resultados), no re-enfocar
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setTimeout(() => { if (!modalOpen) inputRef.current?.focus(); }, 100);
  };

  const stockColor = (s: number) => {
    if (esCotizacion) return { bg: p.accentBg, color: p.accent, label: `Disp: ${s}` };
    if (s === 0) return { bg: p.errorBg, color: p.error, label: "Sin stock" };
    if (s <= 5) return { bg: p.warningBg, color: p.warning, label: `Stock: ${s}` };
    return { bg: p.successBg, color: p.success, label: `Stock: ${s}` };
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
        <TextField
          fullWidth size="small" placeholder="Buscar artículo por nombre o EAN..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} disabled={disabled} autoFocus={!disabled} inputRef={inputRef} onBlur={handleBlur}
          InputProps={{ startAdornment: <InputAdornment position="start">{loading ? <CircularProgress size={16} /> : <SearchIcon sx={{ fontSize: 18, color: p.textT }} />}</InputAdornment> }}
          sx={{ "& .MuiInputBase-root": { fontSize: "0.84rem", borderRadius: "8px", bgcolor: p.bgS, border: `1px solid ${p.border}`, "&:hover": { borderColor: p.borderH }, "&.Mui-focused": { borderColor: p.accent, boxShadow: `0 0 0 3px ${alpha(p.accent, 0.1)}` }, "& fieldset": { border: "none" } } }}
        />

        {showResults && articulos.length > 0 && (
          <Fade in><Paper elevation={0} sx={{ position: "absolute", top: "100%", left: 0, right: 0, mt: 0.5, maxHeight: 400, overflow: "auto", zIndex: 1000, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
            <List sx={{ p: 0 }}>
              {articulos.map((a) => {
                const sc = stockColor(a.stock);
                return (
                  <ListItem key={a.Id} disablePadding>
                    <ListItemButton onClick={() => handleSelect(a)} disabled={!esCotizacion && a.stock === 0}
                      sx={{ py: 1.5, px: 2, "&:hover": { bgcolor: p.hover }, "&.Mui-disabled": { opacity: 0.4 } }}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: "8px", bgcolor: p.bgT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", fontWeight: 700, color: p.textS, flexShrink: 0 }}>{a.Nombre[0]}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: p.text }}>{a.Nombre}</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ fontSize: "0.72rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>EAN: {a.EAN}</Typography>
                            <Box sx={{ display: "inline-flex", px: 0.8, py: 0.1, borderRadius: "4px", bgcolor: sc.bg, color: sc.color, fontSize: "0.68rem", fontWeight: 600 }}>{sc.label}</Box>
                          </Stack>
                        </Box>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>{formatMoney(a.PrecioVenta)}</Typography>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper></Fade>
        )}

        {showResults && articulos.length === 0 && !loading && searchTerm.length >= 2 && (
          <Paper elevation={0} sx={{ position: "absolute", top: "100%", left: 0, right: 0, mt: 0.5, p: 3, zIndex: 1000, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.84rem", color: p.textT }}>No se encontraron artículos</Typography>
          </Paper>
        )}

        {/* Modal de cantidad */}
        <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${p.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>{esCotizacion ? "Agregar a Cotización" : "Agregar Artículo"}</Typography>
            <IconButton onClick={handleClose} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>

          <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
            {selected && (
              <Stack spacing={3}>
                {/* Info artículo */}
                <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", fontWeight: 700, color: p.accent }}>{selected.Nombre[0]}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 650, color: p.text }}>{selected.Nombre}</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>EAN: {selected.EAN}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(selected.PrecioVenta)}</Typography>
                  </Stack>
                  <Box sx={{ mt: 1.5, display: "inline-flex", px: 1.2, py: 0.3, borderRadius: "6px", ...(() => { const sc = stockColor(selected.stock); return { bgcolor: sc.bg, color: sc.color }; })(), fontSize: "0.75rem", fontWeight: 600 }}>
                    {stockColor(selected.stock).label}
                  </Box>
                </Box>

                {/* Cantidad */}
                <Box>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Cantidad</Typography>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ p: 2, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
                    <IconButton onClick={() => cantidad > 1 && setCantidad(cantidad - 1)} disabled={cantidad <= 1}
                      sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: p.errorBg, color: p.error, "&:hover": { bgcolor: alpha(p.error, 0.15) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: p.text, minWidth: 60, textAlign: "center", fontFamily: "'IBM Plex Mono', monospace" }}>{cantidad}</Typography>
                    <IconButton onClick={() => (esCotizacion || cantidad < selected.stock) && setCantidad(cantidad + 1)} disabled={!esCotizacion && cantidad >= selected.stock}
                      sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: p.successBg, color: p.success, "&:hover": { bgcolor: alpha(p.success, 0.15) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                      <AddIcon />
                    </IconButton>
                  </Stack>
                  {!esCotizacion && cantidad >= selected.stock && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "8px", bgcolor: p.warningBg, border: `1px solid ${alpha(p.warning, 0.2)}` }}>
                      <Typography sx={{ fontSize: "0.78rem", color: p.warning, fontWeight: 500 }}>Stock máximo alcanzado</Typography>
                    </Box>
                  )}
                  {esCotizacion && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                      <Typography sx={{ fontSize: "0.78rem", color: p.accent, fontWeight: 500 }}>Cotización: cantidad sin límite de stock</Typography>
                    </Box>
                  )}
                </Box>

                {/* Subtotal */}
                <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.success}`, bgcolor: p.successBg }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace", mt: 0.3 }}>
                    {formatMoney(selected.PrecioVenta * cantidad)}
                  </Typography>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button onClick={handleClose} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>Cancelar</Button>
            <Button onClick={handleAdd} variant="contained" disableElevation startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: p.success, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#5DC0AD" : "#0A6B5E" } }}>
              {esCotizacion ? "Agregar a Cotización" : "Agregar al Carrito"}
            </Button>
          </Box>
        </Dialog>
      </Box>
  );
};
